import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, ImagePlus, Paperclip, Mic, X, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useVoiceRecorder, fmtDuration } from '@/hooks/useVoiceRecorder';

const MAX_ATTACHMENTS = 4;

const IconBtn = ({ onClick, disabled, testId, label, children, className = '' }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} aria-label={label} title={label}
          className={`w-10 h-10 rounded-full grid place-items-center shrink-0 text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-40 transition-[background-color,color,transform] active:scale-90 ${className}`}>
    {children}
  </button>
);

async function upload(path, file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post(path, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data;
}

export default function Composer({ onSend, onTyping, disabled }) {
  const [text, setText] = useState('');
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef();
  const pdfRef = useRef();

  const voice = useVoiceRecorder({
    onDone: async (blob, duration) => {
      setUploading(true);
      try {
        const ext = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm';
        const data = await upload('/upload/file', new File([blob], `vocale.${ext}`, { type: blob.type }));
        await onSend({ content: '', metas: [{ file_id: data.file_id, kind: 'audio', name: data.name, size: data.size, duration, previewUrl: URL.createObjectURL(blob) }], voiceDuration: duration });
      } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
    },
  });

  const addFiles = async (fileList, kind) => {
    const files = Array.from(fileList || []).slice(0, MAX_ATTACHMENTS - items.length);
    if (!files.length) { toast.error(`Massimo ${MAX_ATTACHMENTS} allegati per messaggio`); return; }
    setUploading(true);
    try {
      for (const f of files) {
        if (kind === 'image' && !f.type.startsWith('image/')) { toast.error(`"${f.name}" non è un'immagine`); continue; }
        if (kind === 'pdf' && f.type !== 'application/pdf') { toast.error(`"${f.name}" non è un PDF`); continue; }
        const data = await upload(kind === 'image' ? '/upload/image' : '/upload/file', f);
        setItems(a => [...a, { file_id: data.file_id, kind, name: f.name, size: f.size, previewUrl: kind === 'image' ? URL.createObjectURL(f) : null }].slice(0, MAX_ATTACHMENTS));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); if (imgRef.current) imgRef.current.value = ''; if (pdfRef.current) pdfRef.current.value = ''; }
  };

  const submit = async () => {
    if ((!text.trim() && !items.length) || uploading) return;
    const payload = { content: text.trim(), metas: items };
    setText(''); setItems([]);
    const ok = await onSend(payload);
    if (ok === false) { setText(payload.content); setItems(payload.metas); }
  };

  const startVoice = async () => {
    try { await voice.start(); } catch { toast.error('Microfono non disponibile: controlla i permessi del browser'); }
  };

  if (disabled) return null;

  return (
    <div className="shrink-0 border-t border-border bg-card/90 backdrop-blur-md p-2.5 sm:p-3">
      <AnimatePresence>
        {items.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                      className="flex flex-wrap gap-2 mb-2 px-1" data-testid="msg-attachments-preview">
            {items.map((a, i) => (
              <div key={a.file_id} className="relative">
                {a.kind === 'image'
                  ? <img src={a.previewUrl} alt={a.name} className="w-14 h-14 rounded-xl object-cover border border-border"/>
                  : <div className="h-14 max-w-[180px] rounded-xl border border-terracotta/30 bg-terracotta/10 px-3 flex items-center gap-2 text-xs">
                      <FileText size={16} className="text-terracotta shrink-0"/><span className="truncate">{a.name}</span>
                    </div>}
                <button onClick={() => setItems(x => x.filter((_, j) => j !== i))} aria-label="Rimuovi" data-testid={`msg-attachment-remove-${i}`}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-foreground text-background grid place-items-center hover:scale-110 transition-transform"><X size={10}/></button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {voice.recording ? (
        <div className="flex items-center gap-2" data-testid="voice-recording-bar">
          <IconBtn onClick={voice.cancel} testId="voice-cancel-btn" label="Annulla" className="text-destructive hover:text-destructive"><Trash2 size={17}/></IconBtn>
          <div className="flex-1 h-11 rounded-full bg-background border border-destructive/30 flex items-center gap-3 px-4">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"/>
            <span className="font-mono text-sm tabular-nums" data-testid="voice-timer">{fmtDuration(voice.elapsed)}</span>
            <div className="flex items-center gap-[3px] h-6 flex-1 overflow-hidden">
              {Array.from({ length: 36 }).map((_, i) => (
                <motion.span key={i} className="w-[3px] h-full rounded-full bg-chartreuse/70"
                             animate={{ scaleY: [0.2, 0.4 + ((i * 7) % 10) / 14, 0.2] }} transition={{ repeat: Infinity, duration: 0.7 + (i % 4) * 0.12, delay: i * 0.02 }}/>
              ))}
            </div>
          </div>
          <button onClick={voice.stop} data-testid="voice-send-btn" aria-label="Invia vocale"
                  className="w-11 h-11 rounded-full grid place-items-center bg-chartreuse text-obsidian shrink-0 transition-transform hover:scale-105 active:scale-95">
            <Send size={16}/>
          </button>
        </div>
      ) : (
        <div className="flex items-end gap-1.5">
          <input ref={imgRef} type="file" accept="image/*" multiple className="hidden" onChange={e => addFiles(e.target.files, 'image')} data-testid="msg-file-input"/>
          <input ref={pdfRef} type="file" accept="application/pdf" multiple className="hidden" onChange={e => addFiles(e.target.files, 'pdf')} data-testid="msg-pdf-input"/>
          <IconBtn onClick={() => imgRef.current?.click()} disabled={uploading || items.length >= MAX_ATTACHMENTS} testId="msg-attach-btn" label="Allega foto"><ImagePlus size={17}/></IconBtn>
          <IconBtn onClick={() => pdfRef.current?.click()} disabled={uploading || items.length >= MAX_ATTACHMENTS} testId="msg-attach-pdf-btn" label="Allega PDF"><Paperclip size={17}/></IconBtn>
          <textarea rows={1} value={text}
                    onChange={e => { setText(e.target.value); onTyping?.(); e.target.style.height = 'auto'; e.target.style.height = `${Math.min(140, e.target.scrollHeight)}px`; }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
                    placeholder={items.length ? 'Aggiungi un messaggio (opzionale)…' : 'Scrivi un messaggio…'}
                    data-testid="msg-input"
                    className="flex-1 min-w-0 resize-none rounded-2xl bg-background border border-border px-4 py-2.5 text-sm leading-relaxed outline-none focus:ring-1 focus:ring-chartreuse max-h-36"/>
          {text.trim() || items.length ? (
            <motion.button initial={{ scale: 0.6 }} animate={{ scale: 1 }} onClick={submit} disabled={uploading} data-testid="msg-send-btn" aria-label="Invia"
                           className="w-11 h-11 rounded-full grid place-items-center bg-chartreuse text-obsidian shrink-0 shadow-[0_6px_24px_-6px_rgba(180,255,0,0.7)] disabled:opacity-50 transition-transform hover:scale-105 active:scale-95">
              <Send size={16}/>
            </motion.button>
          ) : (
            <button onClick={startVoice} disabled={uploading} data-testid="msg-mic-btn" aria-label="Registra vocale"
                    className="w-11 h-11 rounded-full grid place-items-center bg-secondary text-foreground shrink-0 hover:bg-chartreuse hover:text-obsidian transition-[background-color,color,transform] active:scale-95 disabled:opacity-50">
              <Mic size={17}/>
            </button>
          )}
        </div>
      )}
      {uploading && <div className="text-[10px] text-muted-foreground pt-1.5 px-2 animate-pulse">Caricamento…</div>}
    </div>
  );
}
